import {
  AUTH_TOKEN,
  IMPERSONATION_AUTH_TOKEN,
  IMPERSONATION_REFRESH_TOKEN,
  IMPERSONATION_SESSION,
  REFRESH_TOKEN,
} from '@repo/constants';
import { ImpersonationState } from '@repo/types';

const IMPERSONATION_EXPIRED_NOTICE = 'impersonation-expired-notice';
const IMPERSONATION_EXPIRED_MESSAGE = 'Impersonation session expired';

/**
 * True when the current tab holds impersonation tokens in `sessionStorage`.
 * Impersonation tokens live in `sessionStorage` so they stay scoped to the
 * tab that started the flow — other tabs on the same origin keep using the
 * user's own session from `localStorage`.
 */
export const isImpersonationActive = (): boolean =>
  Boolean(sessionStorage.getItem(IMPERSONATION_AUTH_TOKEN));

export const getActiveAuthToken = (): string | null =>
  sessionStorage.getItem(IMPERSONATION_AUTH_TOKEN) ??
  localStorage.getItem(AUTH_TOKEN);

export const getActiveRefreshToken = (): string | null =>
  sessionStorage.getItem(IMPERSONATION_REFRESH_TOKEN) ??
  localStorage.getItem(REFRESH_TOKEN);

export const hasAnyAuthToken = (): boolean => Boolean(getActiveAuthToken());

export const setImpersonationTokens = (token: string, refresh: string): void => {
  sessionStorage.setItem(IMPERSONATION_AUTH_TOKEN, token);
  sessionStorage.setItem(IMPERSONATION_REFRESH_TOKEN, refresh);
};

/**
 * Write refreshed tokens back to whichever store the current tab is using.
 * Used by the axios refresh-token flow so an impersonation tab keeps its
 * rotated tokens in `sessionStorage` and never leaks them into `localStorage`.
 */
export const setActiveAuthTokens = (token: string, refresh: string): void => {
  if (isImpersonationActive()) {
    sessionStorage.setItem(IMPERSONATION_AUTH_TOKEN, token);
    sessionStorage.setItem(IMPERSONATION_REFRESH_TOKEN, refresh);
    return;
  }
  localStorage.setItem(AUTH_TOKEN, token);
  localStorage.setItem(REFRESH_TOKEN, refresh);
};

export const clearImpersonationSession = (): void => {
  sessionStorage.removeItem(IMPERSONATION_AUTH_TOKEN);
  sessionStorage.removeItem(IMPERSONATION_REFRESH_TOKEN);
  sessionStorage.removeItem(IMPERSONATION_SESSION);
};

export const clearOwnSession = (): void => {
  localStorage.removeItem(AUTH_TOKEN);
  localStorage.removeItem(REFRESH_TOKEN);
  localStorage.removeItem('clientId');
};

/**
 * Defensive cleanup that clears both stores. Use on fresh logins and any flow
 * where the caller does not care which session was active.
 */
export const clearAuthState = (): void => {
  clearImpersonationSession();
  clearOwnSession();
};

export const consumeImpersonationExpiredNotice = (): string | undefined => {
  const notice = sessionStorage.getItem(IMPERSONATION_EXPIRED_NOTICE);

  if (!notice) {
    return undefined;
  }

  sessionStorage.removeItem(IMPERSONATION_EXPIRED_NOTICE);
  return notice;
};

export const getStoredImpersonationSession = ():
  | Extract<ImpersonationState, { active: true }>
  | undefined => {
  const rawValue = sessionStorage.getItem(IMPERSONATION_SESSION);

  if (!rawValue) {
    return undefined;
  }

  try {
    const parsedValue = JSON.parse(rawValue) as ImpersonationState;

    if (parsedValue.active !== true) {
      sessionStorage.removeItem(IMPERSONATION_SESSION);
      return undefined;
    }

    return parsedValue;
  } catch {
    sessionStorage.removeItem(IMPERSONATION_SESSION);
    return undefined;
  }
};

export const setStoredImpersonationSession = (session?: ImpersonationState) => {
  if (!session || session.active !== true) {
    sessionStorage.removeItem(IMPERSONATION_SESSION);
    return;
  }

  sessionStorage.setItem(IMPERSONATION_SESSION, JSON.stringify(session));
};

export const redirectAfterAuthExpired = (loginPath = '/auth/login') => {
  const impersonationSession = getStoredImpersonationSession();
  const wasImpersonationTab = isImpersonationActive();

  if (wasImpersonationTab) {
    // Only clear sessionStorage so the impersonator's own session (in
    // localStorage on this origin) and any sibling tabs stay alive.
    clearImpersonationSession();

    if (impersonationSession?.returnUrl) {
      window.location.href = impersonationSession.returnUrl;
      return;
    }

    sessionStorage.setItem(
      IMPERSONATION_EXPIRED_NOTICE,
      IMPERSONATION_EXPIRED_MESSAGE,
    );

    if (window.location.pathname !== loginPath) {
      window.location.href = loginPath;
    }
    return;
  }

  clearOwnSession();

  if (window.location.pathname !== loginPath) {
    window.location.href = loginPath;
  }
};
